import { OFFICE_TOKEN_INVALID, OFFICE_USER_INACTIVE, OFFICE_USER_UNKNOWN, OfficeSessionError } from "./officeSession.ts";
/** Tokens are renewed this long before they expire. */ export const TOKEN_EXPIRY_MARGIN_MS = 60_000;
export class AuthError extends Error {
    constructor(code, message){
        super(message);
        this.name = "AuthError";
        this.code = code;
    }
    code;
}
const isFresh = (token)=>token !== null && token.expiresAt - Date.now() > TOKEN_EXPIRY_MARGIN_MS;
const isRejectedAuth0Token = (error)=>error instanceof OfficeSessionError && error.code === OFFICE_TOKEN_INVALID;
function signedOutReason(error) {
    if (error instanceof AuthError) return "expired";
    if (!(error instanceof OfficeSessionError)) return null;
    if (error.code === OFFICE_USER_UNKNOWN) return "unknownUser";
    if (error.code === OFFICE_USER_INACTIVE) return "inactive";
    if (error.status === 401 || error.status === 403) return "expired";
    return null;
}
/**
 * Owns the gateway token. It lives in memory only and is minted from the
 * Auth0 access token at `POST /auth/office/session`; the Auth0 tokens and the
 * rotating refresh token live in the Auth0 client's storage.
 *
 * `getGatewayToken()` is single-flight: every caller during a renewal shares it.
 * A refused refresh token, a twice-refused exchange or an unknown or inactive
 * user signs out; anything else (offline, 429, 5xx) fails only that call.
 */ export class TokenManager {
    getState() {
        return this.state;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return ()=>this.listeners.delete(listener);
    }
    /** Signs in from the stored Auth0 session, if there is one. Never throws. */ async initialize() {
        await this.getGatewayToken().catch(()=>undefined);
    }
    getGatewayToken() {
        if (isFresh(this.gatewayToken)) {
            return Promise.resolve(this.gatewayToken.value);
        }
        this.inflight ??= this.renew().finally(()=>{
            this.inflight = null;
        });
        return this.inflight;
    }
    /** Forgets a token the gateway refused, unless it was already replaced. */ invalidateGatewayToken(rejected) {
        if (this.gatewayToken?.value === rejected) {
            this.gatewayToken = null;
        }
    }
    async signOut() {
        const token = this.gatewayToken?.value;
        await this.clear(null);
        if (token) {
            await this.deps.revoke(token).catch(()=>undefined);
        }
    }
    async renew() {
        try {
            const session = await this.exchange(false).catch((error)=>{
                if (!isRejectedAuth0Token(error)) throw error;
                return this.exchange(true);
            });
            this.gatewayToken = {
                value: session.access_token,
                expiresAt: Date.now() + session.expires_in * 1000
            };
            if (this.state.status !== "signedIn") {
                this.setState({
                    status: "signedIn",
                    session
                });
            }
            return session.access_token;
        } catch (error) {
            await this.failed(error);
            throw error;
        }
    }
    async exchange(force) {
        return this.deps.exchange(await this.deps.auth0AccessToken({
            force
        }));
    }
    async failed(error) {
        const reason = signedOutReason(error);
        const wasSignedIn = this.state.status === "signedIn";
        if (error instanceof AuthError && error.code === "notSignedIn" && !wasSignedIn) {
            await this.clear(null);
        } else if (reason) {
            await this.clear(reason);
        } else if (!wasSignedIn) {
            this.setState({
                status: "signedOut",
                reason: "unavailable"
            });
        }
    }
    async clear(reason) {
        this.gatewayToken = null;
        await this.deps.clearAuth0().catch(()=>undefined);
        this.setState({
            status: "signedOut",
            reason
        });
    }
    setState(state) {
        this.state = state;
        this.listeners.forEach((listener)=>listener(state));
    }
    constructor(deps){
        this.deps = deps;
    }
    state = {
        status: "restoring"
    };
    listeners = new Set();
    gatewayToken = null;
    inflight = null;
    deps;
}
/** Sends a gateway request with a fresh token; on a 401, renews once and resends. */ export async function sendWithGatewayToken(manager, send) {
    const token = await manager.getGatewayToken();
    const response = await send(token);
    if (response.status !== 401) {
        return response;
    }
    manager.invalidateGatewayToken(token);
    return send(await manager.getGatewayToken());
}
